<template>
  <div v-if="editor" id="discussion-editor">
    <div id="editor-wrapper" class="editor relative" @click="closeBlockColorPicker">
      <MenuFloating
        v-if="editable"
        ref="menuFloating"
        :discussion="discussion"
        :book-id="discussion.bookId"
        :editor="editor"
        :allow-upload="allowUpload"
      />

      <MenuBubble v-if="editable" :editor="editor" :is-table-active="!!isTableActive" is-event />

      <editor-content
        id="editor"
        autocomplete="new password"
        autocorrect="off"
        autocapitalize="on"
        :spellcheck="isGrammarCheck"
        class="editor__content h-full font-main text-canvas-color text-main flex flex-col flex-grow"
        :class="[settings.fontFamily === 'Silka mono' ? 'silka-font': 'tracking-normal']"
        :editor="editor"
      />

      <TableControls
        v-if="!!isTableActive && editable"
        :key="isTableActive"
        :style="{ top: `${ tableTop }px` }"
        :editor="editor"
        :active-table="activeTable"
      />

      <ProjectInstruction />
      <TemplateModal :editor="editor" />

      <!-- mention -->
      <div v-show="showMentions" ref="mentionRef" :class="isDarkMode ? 'bg-dark-800' : 'bg-white'">
        <template v-if="hasMentions">
          <div
            v-for="(item, index) in filteredMentions"
            :key="item.id"
            :ref="el => mentionRefs[index] = el"
            class="flex items-center py-5 px-10 cursor-pointer last:mb-0 no-scroll-bar last:rounded-b-10 text-14"
            :class="[
              isDarkMode ? 'text-grey-500' : 'text-dark-600',
              {'bg-selection font-semibold': navigatedMentionIndex === index},
              {'mention-project': item.label === 'pages'},
              {'mention-user': item.label === 'users'}
            ]"
            @mouseenter="onHover"
            @mouseleave="outHover(index, $event)"
            @click.stop="selectMention(item)"
          >
            <div v-if="item.label === 'users'">
              <MentionUser :user="item" />
            </div>
            <div v-else class="flex items-center">
              <img v-if="item.icon" class="w-20 h-20 mr-10" :src="require(`@/assets/images/icons/${item.icon}.png`)" alt="icon">
              <img v-else class="w-20 h-20 mr-10" src="@/assets/images/icons/purple_circle.png" alt="icon">
              <span> {{ item.title }} </span>
            </div>
          </div>
        </template>
      </div>
      <!-- emoji -->
      <div v-show="showEmoji" ref="emojiRef" :class="isDarkMode ? 'bg-dark-800' : 'bg-white'">
        <template v-if="hasResults">
          <div
            v-for="(item, index) in filteredItems"
            :key="item.id"
            :ref="el => emojiRefs[index] = el"
            class="px-20 py-10 cursor-pointer last:mb-0 no-scroll-bar first:rounded-t-4 last:rounded-b-4"
            :class="[isDarkMode ? 'text-grey-500' : 'text-grey-main', {'bg-selection': navigatedEmojiIndex === index}]"
            @mouseenter="onHover"
            @mouseleave="outHover(index, $event)"
            @click.stop="selectEmoji(item, editor)"
          >
            {{ item.char }} <span class="ml-5">:{{ item.name }}:</span>
          </div>
        </template>
      </div>

      <portal-target name="editor" multiple />
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-3'
import { discussionExtensions } from '@/tiptap/extensions'

import MenuBubble from '@/components/workspace/MenuBubble'
import MenuFloating from '@/components/workspace/MenuFloating'
import TableControls from '@/components/workspace/TableControls.vue'
import MentionUser from '@/components/workspace/MentionUser'

import TemplateModal from '@/components/templates/TemplateModal'

import ProjectInstruction from '@/components/instructions/ProjectInstruction'

import MentionMixin from '@/mixins/MentionMixin.js'
import EmojiMixin from '@/mixins/EmojiMixin.js'
import ImageUploadMixin from '@/mixins/ImageUploadMixin.js'

import { attachmentService } from '@/services'

import designConfig from '@/core/design-config'

import { defineComponent, computed, ref, watch, onBeforeUnmount, onMounted } from 'vue'
import { useStore } from 'vuex'
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRoute } from 'vue-router'

export default defineComponent({
  components: {
    EditorContent,
    MenuBubble,
    MenuFloating,
    ProjectInstruction,
    TableControls,
    TemplateModal,
    MentionUser
  },

  props: {
    discussion: Object,
    bookId: Number,
    editable: { type: Boolean, default: true }
  },

  emits: ['updateContent'],

  setup (props, { emit }) {
    const { state, getters, commit } = useStore()
    // const router = useRouter()
    const route = useRoute()

    // mixins
    const emoji = EmojiMixin()
    const mention = MentionMixin()
    const imageUpload = ImageUploadMixin(props)

    // image
    let existingImages = null
    // table
    const activeTable = ref(null)
    const tableTop = ref(0)
    // refs
    const menuFloating = ref(null)
    // computed
    const files = computed(() => state.file.files)
    const book = computed(() => state.book.book)
    const books = computed(() => state.book.books)
    const settings = computed(() => state.settings.settings)
    const isTableActive = computed(() => state.project.isTableActive)
    const isGrammarCheck = computed(() => state.settings.isGrammarCheck)
    const isMobile = computed(() => getters.isMobile)
    const isDarkMode = computed(() => getters.isDarkMode)
    const headquarters = computed(() => state.team.headquarters)

    const allowUpload = computed(() => !!props.discussion.id)

    const editor = new Editor({
      onUpdate: ({ transaction }) => {
        save(editor.getHTML(), editor.getJSON())
        tableTopPosition()
        restoreOrDeleteImage()
      },
      extensions: discussionExtensions({
        highlight: {
          multicolor: true
        },
        dropcursor: {
          color: designConfig.themes[settings.value?.theme]?.linkColor,
          width: 2
        },
        mention: {
          items: () => {
            const currBook = books.value.find(b => b.id === props.bookId)
            let users = currBook?.shareWith

            if (headquarters.value.id === props.bookId) users = state.team.members

            const mappedProjects = book.value.projects.map((p) => ({ ...p, label: 'pages' }))
            const mappedUsers = users.map((u) => ({
              ...u,
              label: 'users',
              title: u.firstName,
              discussionId: props.discussion.id,
              bookId: props.discussion.bookId,
              sendImmediately: true
            }))

            return [...mappedProjects, ...mappedUsers]
          },
          onEnter: mention.onEnter,
          onKeyDown: mention.onKeyDown,
          onChange: mention.onChange,
          onExit: mention.onExit,
          onFilter: mention.onFilter
        },
        taskItem: {
          nested: true
        },
        emoji: {
          onEnter: emoji.onEnter,
          onKeyDown: emoji.onKeyDown,
          onChange: emoji.onChange,
          onExit: emoji.onExit,
          onFilter: emoji.onFilter
        },
        image: {
          uploadFunc: allowUpload.value ? imageUpload.upload : null
        }
      }),
      disablePasteRules: ['italic'],
      content: props.discussion.content || '',
      editable: props.editable
    })

    const highlightedText = computed(() => state.general.highlightedText)

    watch([
      () => route.query.content,
      () => highlightedText.value
    ], value => {
      if (value) {
        setTimeout(() => scrollToHighlightedText(), 300)
      }
    })

    const scrollToHighlightedText = () => {
      if (route.query.content && editor) {
        editor.commands.setSearchTerm(highlightedText.value)

        const el = document.getElementsByClassName('search-result')[0]
        if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' })

        setTimeout(() => {
          editor.commands.setSearchTerm('')
          commit('setHighlightedText', '')
        }, 4000)
      }
    }

    onMounted(() => {
      existingImages = checkImages(editor.getJSON())

      setTimeout(() => scrollToHighlightedText(), 500)
    })

    watch(isTableActive, (active) => {
      if (active) {
        tableTopPosition()
      }
    })

    watch(() => props.discussion.content, (value) => {
      if (!editor.isFocused) {
        editor.commands.setContent(value)
      }
    })

    onBeforeUnmount(async () => {
      editor.destroy()
      commit('setTableActive', false)
    })

    onBeforeRouteLeave((to) => {
      if (to.name !== 'Login') clearAttachments()

      if (Object.values(files.value).some(item => item.percent >= 0 && item.bookId === book.value.id)) {
        commit('setNotification', {
          isVisible: true,
          message: 'Your file is still uploading, please wait for it to finish'
        })
        return false
      }
    })

    onBeforeRouteUpdate(() => {
      clearAttachments()

      if (Object.values(files.value).some(item => item.percent >= 0 && item.bookId === book.value.id)) {
        commit('setNotification', {
          isVisible: true,
          message: 'Your file is still uploading, please wait for it to finish'
        })
        return false
      }
    })

    function clearAttachments () {
      attachmentService.clear({
        discussionId: props.discussion.id,
        data: { ids: existingImages }
      })
    }

    function restoreOrDeleteImage () {
      const newImages = checkImages(editor.getJSON())

      if (existingImages?.length > newImages?.length) {
        const imageId = existingImages.filter(imageId => !newImages.includes(imageId))[0]

        commit('deleteFile', imageId)

        if (imageId) {
          attachmentService.delete({
            discussionId: props.discussion.id,
            data: { id: imageId },
            bookId: props.discussion.bookId
          })
        }
      } else if (newImages?.length > existingImages?.length) {
        const imageId = newImages.filter(imageId => !existingImages.includes(imageId))[0]

        if (imageId) {
          attachmentService.restore({
            discussionId: props.discussion.id,
            data: { id: imageId },
            bookId: props.discussion.bookId
          })
        }
      }

      existingImages = newImages
    }

    function checkImages (json) {
      let nodes = []

      json.content
        .filter(item => item.type === 'paragraph' && item.content?.length)
        .map(item => item.content)
        .forEach(item => nodes.push(...item))

      nodes = nodes.filter(item => item.type === 'image' || item.type === 'file')

      return nodes.map(item => +item.attrs.id)
    }

    function tableTopPosition () {
      if (isTableActive.value) {
        activeTable.value = document.querySelector('.tableWrapper.activeTable table')

        if (!activeTable.value) return

        if (isMobile.value) {
          tableTop.value = (activeTable.value.getBoundingClientRect().bottom - 50) + window.pageYOffset
        } else {
          const main = document.getElementById('main')
          tableTop.value = (activeTable.value.getBoundingClientRect().bottom - 75) + main.scrollTop
        }
      }
    }

    function closeBlockColorPicker () {
      if (menuFloating.value) menuFloating.value.closeBlockColorPicker()
    }

    function save (text) {
      emit('updateContent', text)
    }

    function onHover (e) {
      e.target.style = `background-color: ${designConfig.themes[settings.value.theme].selectionColor}`
    }

    function outHover (index, e) {
      if (emoji.navigatedEmojiIndex.value === index) {
        e.target.style = `background-color: ${designConfig.themes[settings.value.theme].selectionColor}`
      } else {
        e.target.style = 'background-color: transparent'
      }
    }

    return {
      // mention
      navigatedMentionIndex: mention.navigatedMentionIndex,
      filteredMentions: mention.filteredMentions,
      selectMention: (item) => mention.selectMention(item, editor),
      showMentions: mention.showMentions,
      hasMentions: mention.hasMentions,
      mentionRefs: mention.mentionRefs,
      mentionRef: mention.mentionRef,
      // emoji
      navigatedEmojiIndex: emoji.navigatedEmojiIndex,
      filteredItems: emoji.filteredItems,
      selectEmoji: emoji.selectEmoji,
      hasResults: emoji.hasResults,
      showEmoji: emoji.showEmoji,
      emojiRefs: emoji.emojiRefs,
      emojiRef: emoji.emojiRef,
      // data
      activeTable,
      allowUpload,
      tableTop,
      editor,
      // refs
      menuFloating,
      // computed
      settings,
      isTableActive,
      isGrammarCheck,
      isDarkMode,

      // functions
      closeBlockColorPicker,
      onHover,
      outHover
    }
  }
})
</script>
